import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, Download } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import SourceRequestModal from '../components/shared/SourceRequestModal';
import WorkflowRefLink from '../components/shared/WorkflowRefLink';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import RecordActionsCard from '../components/workflow/RecordActionsCard';
import { SectionCard, DetailGrid, DetailItem } from '../components/workflow/SectionCard';
import { RecordTimeline, RecordFormModal } from './QMQcRecords';
import { QC_RECORD_KIND, SourceBadge } from '../components/records/qcRecordKind';
import { engagementLabelsOf } from '../components/workflow/CoverageDisplay';
import { nowLabel } from '../components/shared/NowName';
import { QcFormView } from '../components/workflow/QcForm';
import QcAddendaSection from '../components/records/QcAddendaSection';
import { SignedFormPendingNotice, FormPdfDownload } from '../components/records/legacyImport';
import { ProvenanceBadge, provenanceOrigin } from '../components/shared/provenance';
import { formatDate } from '../utils/dateFormat';
import { fetchJSON, API_BASE_URL } from '../utils/api';

// Management — 一筆 QC6/QC7 記錄的詳情頁。
//
// 閱讀版面有自己的網址,所以
// workflow 詳情頁的「Open in Management →」可以**直接連到這一筆**,不必靠
// 「清單 + 搜尋關鍵字」逼近（那種 workaround 撞名就會列出多筆、改名就失效）。
//
// 編輯與刪除走 modal（`RecordFormModal`),新增走自己的分步頁 QMQcRecordNew——
// 與 workflow 的 New（頁）／Modify（modal）分工相同。兩者的欄位是
// qcRecordKind 的同一組區塊,所以外殼不同不會讓內容漂移;這頁的閱讀列與
// Source modal 欄位也吃同一份設定。
const QMQcRecordDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('qm:qc_records:edit');

  const [record, setRecord] = useState(null);
  const [usedByOutside, setUsedByOutside] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showSource, setShowSource] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await fetchJSON(`/api/qm/qc-records/${id}`);
      setRecord(data.record);
      setUsedByOutside(data.usedByOutside || []);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // 核准產生的記錄永遠刪不得（正式資料鐵則）;手動建的只有在**涵蓋清單以外**
  // 有案子引用時才擋——它自己蓋章的那幾案會在刪除時一併解除。
  const fromWorkflow = record && record.source === 'workflow:qcr';
  const deleteBlocked = fromWorkflow || usedByOutside.length > 0;
  const deleteTitle = fromWorkflow
    ? `Produced by ${record.sourceRequestNo} — approved records cannot be deleted`
    : usedByOutside.length > 0
      ? 'Engagements outside this record’s coverage reference it — cannot delete'
      : 'Delete';

  const current = (record?.files || []).filter((f) => !f.superseded);
  const previous = (record?.files || []).filter((f) => f.superseded);

  return (
    <RequestDetailShell
      backTo="/qm/qc-records"
      backLabel="QC6 / QC7 Records"
      loading={!record}
      error={error}
      notice={notice}
    >
      {record && (
        <>
          <RequestHeader
            requestNo={`${record.qcType} · ${nowLabel(record.entityName, record.entityNameNow)}`}
            originLabel={record.sourceRequestNo
              ? <>produced by <WorkflowRefLink refNo={record.sourceRequestNo} className="text-sm" /></>
              : provenanceOrigin(record.source)}
            submittedAt={record.createdAt}
            badge={<><SourceBadge record={record} onOpen={() => setShowSource(true)} /><ProvenanceBadge source={record.source} /></>}
          />

          <SignedFormPendingNotice pending={record.signedFormPending === true} />

          {canEdit && (
            <RecordActionsCard
              onEdit={() => setEditing(true)}
              onDelete={() => setDeleting(true)}
              deleteDisabled={deleteBlocked}
              deleteTitle={deleteTitle}
            />
          )}

          <SectionCard title="Record">
            <DetailGrid>
              {QC_RECORD_KIND.rows(record, usedByOutside).map(([label, value]) => (
                <DetailItem key={label} label={label}>{value}</DetailItem>
              ))}
            </DetailGrid>
          </SectionCard>

          {(record.answers || record.submissionMethod === 'form') && (
            <SectionCard title="QC6 / QC7 Form">
              {/* 匯入記錄的表大半是空的——在展開鈕上方講一次，讀的人才不會
                  以為資料掉了。上面那格只說簽署檔在不在,不兼這件事。
                  答案直接展開，同 workflow QC 詳情頁。 */}
              {record.legacyImport && (
                <p className="text-sm text-slate-500 mb-2">
                  Answers were taken from the signed form where they could be read; anything blank is on the signed form.
                </p>
              )}
              <QcFormView qcType={record.qcType} answers={record.answers} engagements={record.engagements || []} />
            </SectionCard>
          )}

          {/* 核准後才出現的新資訊——附錄掛在記錄底下，原表答案不動。 */}
          <QcAddendaSection record={record} canEdit={canEdit} onChanged={load} />

          {/* 每個版本的文件都留著：現行的照常讀,被修改單取代
              的移到自己的淡色區塊並指名取代者——「現在簽的是哪一份」和「之前
              簽的是哪一份」在同一頁都答得出來,不必去翻歷程。 */}
          <SectionCard title="Files">
            {record.submissionMethod === 'form' && record.answers && (
              // The form itself, rendered on demand with the sign-off block —
              // never a stored file, so it always prints the current answers.
              <div className="flex items-center gap-3 py-2 mb-1 border-b border-slate-100">
                <FileText className="w-4 h-4 text-blue-500 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-800">Form {record.qcType} (PDF)</div>
                  <span className="text-[10px] text-slate-500">Generated from the record{record.approvedOn ? ` · approved ${formatDate(record.approvedOn)}` : ''}</span>
                </div>
                <FormPdfDownload
                  record={record}
                  formName={record.qcType}
                  href={`${API_BASE_URL}/api/qm/qc-records/${record.id}/pdf`}
                  signedHref={(f) => `${API_BASE_URL}/api/qm/qc-records/${record.id}/files/${f.id}`}
                  className="text-blue-600 hover:text-blue-800 shrink-0" title="Download PDF"
                >
                  <Download className="w-4 h-4" />
                </FormPdfDownload>
              </div>
            )}
            {current.length === 0 && previous.length === 0 ? (
              <p className="text-sm text-slate-500">No files attached.</p>
            ) : (
              <>
                <ul className="divide-y divide-slate-100">
                  {current.map((f) => (
                    <li key={f.id} className="flex items-center gap-3 py-2">
                      <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-slate-800 truncate">{f.originalName}</div>
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
                          {f.docType === 'QC_FORM' ? 'Signed form (uploaded)' : 'Supporting'}
                        </span>
                      </div>
                      <a
                        href={`${API_BASE_URL}/api/qm/qc-records/${record.id}/files/${f.id}`}
                        target="_blank" rel="noreferrer"
                        className="text-blue-600 hover:text-blue-800 shrink-0" title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    </li>
                  ))}
                </ul>
                {previous.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-dashed border-slate-200">
                    <h5 className="text-[11px] font-semibold text-slate-500 uppercase mb-1">Previous Versions</h5>
                    <ul className="divide-y divide-slate-100">
                      {previous.map((f) => (
                        <li key={f.id} className="flex items-center gap-3 py-1.5">
                          <FileText className="w-4 h-4 text-slate-300 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm text-slate-500 truncate">{f.originalName}</div>
                            <span className="text-[10px] text-slate-500">
                              {f.docType === 'QC_FORM' ? 'Signed form (uploaded)' : 'Supporting'}
                              {f.supersededByRef ? ` · replaced by ${f.supersededByRef}` : ' · replaced'}
                              {f.supersededAt ? ` on ${formatDate(f.supersededAt)}` : ''}
                            </span>
                          </div>
                          <a
                            href={`${API_BASE_URL}/api/qm/qc-records/${record.id}/files/${f.id}`}
                            target="_blank" rel="noreferrer"
                            className="text-slate-400 hover:text-slate-600 shrink-0" title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </SectionCard>

          <RecordTimeline recordId={record.id} qcType={record.qcType} engagements={record.engagements || []} />

          {editing && (
            <RecordFormModal
              record={record}
              onClose={() => setEditing(false)}
              onSaved={(_saved, applyMessage) => {
                setEditing(false);
                setNotice(applyMessage || 'Record updated.');
                load();
              }}
            />
          )}
          {showSource && (
            <SourceRequestModal
              title={`Source Application · ${record.sourceRequestNo}`}
              fetchPath={`/api/qm/qc-records/${record.id}/source`}
              openHref={`/workflow/qc/${record.sourceRequestId}`}
              fields={QC_RECORD_KIND.sourceFields}
              onClose={() => setShowSource(false)}
            />
          )}
          {deleting && (
            <ConfirmDeleteModal
              title="Delete QC Record"
              message={(() => {
                const base = `Delete the ${record.qcType} record for "${record.entityName}" (FYE ${formatDate(record.yearEnd)})? Its files are removed too.`;
                const covered = (record.engagements || []).filter((e) => e.engagementId);
                const labels = engagementLabelsOf(covered);
                // 刪除會連帶解除它蓋過章的案子——那是最容易被忽略的後果,講在前面。
                return covered.length === 0 ? base
                  : `${base} The QC link is also cleared from ${covered.length} engagement${covered.length === 1 ? '' : 's'} (${covered.map((e) => labels[e.engagementId].text).join(', ')}), which will need a QC6 / QC7 form again.`;
              })()}
              onClose={() => setDeleting(false)}
              onConfirm={async () => {
                await fetchJSON(`/api/qm/qc-records/${record.id}`, { method: 'DELETE' });
                navigate('/qm/qc-records');
              }}
            />
          )}
        </>
      )}
    </RequestDetailShell>
  );
};

export default QMQcRecordDetail;
